import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  EmailLogService,
  EmailLogRequestDTO,
  EmailLogResponseDTO,
  PaginatedEmailLogResponse
} from '../../../services/email-log.service';

@Component({
  selector: 'app-email-logs',
  templateUrl: './email-logs.component.html',
  styleUrls: ['./email-logs.component.scss']
})
export class EmailLogsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // Estado de datos
  emailLogs: EmailLogResponseDTO[] = [];
  totalRecords = 0;
  loading = false;
  error: string | null = null;

  // Paginación
  currentPage = 1;
  pageSize = 25;
  totalPages = 0;

  // Filtros
  filters: EmailLogRequestDTO = {
    page: 1,
    pageSize: 25,
    orderBy: 'SentAt',
    orderDirection: 'desc'
  };

  // Opciones de filtro
  emailTypeOptions = [
    { value: 1, label: 'Link de Pago - Participante' },
    { value: 2, label: 'Link de Pago - Empresa' },
    { value: 3, label: 'Recordatorio de Pago' },
    { value: 4, label: 'Genérico' }
  ];

  // Estado de la UI
  showFilters = true;
  selectedLog: EmailLogResponseDTO | null = null;
  showLogDetail = false;

  // Configuración de fechas
  maxDate = new Date();
  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  constructor(public emailLogService: EmailLogService) {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.filters.recipientEmail = searchTerm || undefined;
      this.currentPage = 1;
      this.filters.page = 1;
      this.loadEmailLogs();
    });
  }

  ngOnInit(): void {
    this.loadEmailLogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadEmailLogs(): void {
    this.loading = true;
    this.error = null;

    this.filters.page = this.currentPage;
    this.filters.pageSize = this.pageSize;

    if (this.dateFrom) {
      this.filters.dateFrom = this.dateFrom;
    }
    if (this.dateTo) {
      const endOfDay = new Date(this.dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      this.filters.dateTo = endOfDay;
    }

    this.emailLogService.searchEmailLogs(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PaginatedEmailLogResponse) => {
          this.emailLogs = response.data;
          this.totalRecords = response.totalRecords;
          this.totalPages = response.totalPages;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading email logs:', error);
          this.error = 'Error al cargar los logs de email';
          this.loading = false;
        }
      });
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchSubject.next(target.value);
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadEmailLogs();
    }
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = parseInt(target.value);
    this.currentPage = 1;
    this.loadEmailLogs();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.filters.page = 1;
    this.loadEmailLogs();
  }

  clearFilters(): void {
    this.filters = {
      page: 1,
      pageSize: this.pageSize,
      orderBy: 'SentAt',
      orderDirection: 'desc'
    };
    this.dateFrom = null;
    this.dateTo = null;
    this.currentPage = 1;
    this.loadEmailLogs();
  }

  changeSort(column: string): void {
    if (this.filters.orderBy === column) {
      this.filters.orderDirection = this.filters.orderDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.filters.orderBy = column;
      this.filters.orderDirection = 'desc';
    }
    this.loadEmailLogs();
  }

  showDetail(log: EmailLogResponseDTO): void {
    this.selectedLog = log;
    this.showLogDetail = true;
  }

  closeDetail(): void {
    this.showLogDetail = false;
    this.selectedLog = null;
  }

  setQuickFilter(type: string): void {
    this.clearFilters();

    switch (type) {
      case 'errors':
        this.filters.isSuccess = false;
        break;
      case 'success':
        this.filters.isSuccess = true;
        break;
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.dateFrom = today;
        this.dateTo = new Date();
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        this.dateFrom = weekAgo;
        this.dateTo = new Date();
        break;
      case 'reminders':
        this.filters.emailType = 3;
        break;
      case 'payment-links':
        this.filters.emailType = 1;
        break;
    }

    this.applyFilters();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  }

  downloadLogs(): void {
    const dataStr = JSON.stringify(this.emailLogs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `email-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  trackByLogId(index: number, log: EmailLogResponseDTO): number {
    return log.id;
  }

  Math = Math;
  JSON = JSON;
}
